import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { tokenIsExpired } from "../../authUtils";
import { Typography, Box, Container, IconButton, Tooltip } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CopyrightIcon from '@mui/icons-material/Copyright';
import NavigationCard from "../NavigationCard";
import SearchBar from "../SearchBar";
import EmptySearchState from "../EmptySearchState";
import FavoritesSection from "../FavoritesSection";
import navigationConfig, { filterNavigationByRole } from "../../navigationConfig";
import { useTheme } from "../../contexts/ThemeContext";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import ThemeSwitcher from "../ThemeSwitcher";
import ViewModeSelector from "../ViewModeSelector";
import SettingsPanel from "../SettingsPanel";
import { globalStyles } from "../../themes/themeConfig";
import { filterNavigationBySearch, debounce } from "../../utils/searchUtils";

const Home = () => {
    const { isAuthenticated, logout, token, roleName, username } = useAuth();
    const { themeStyles } = useTheme();
    const { preferences, updatePreferences, resetPreferences } = useUserPreferences();
    const history = useHistory();
    const [selectedCard, setSelectedCard] = useState(null);
    const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const searchInputRef = React.useRef(null);

    // Calculate grid layout based on view mode and preferences
    const gridLayout = useMemo(() => {
        const { viewMode, cardSize, cardSpacing } = preferences;

        // Base card width calculation (in pixels)
        const baseCardWidth = 120 + (cardSize * 30); // 120px base + size multiplier

        // Calculate responsive columns for grid view based on breakpoints
        const calculateGridColumns = () => {
            if (viewMode === 'list') {
                return 1; // List view always single column
            }

            // Responsive columns based on viewport and view mode
            // These will be used in sx prop with breakpoint syntax
            if (viewMode === 'grid') {
                return {
                    xs: 2,  // Mobile: 2 columns
                    sm: 3,  // Tablet: 3 columns
                    md: selectedCard ? 4 : 5,  // Desktop: 4-5 columns
                    lg: selectedCard ? 5 : 6,  // Large: 5-6 columns
                    xl: selectedCard ? 6 : 7,  // XL: 6-7 columns
                };
            } else if (viewMode === 'compact') {
                return {
                    xs: 3,  // Mobile: 3 columns
                    sm: 4,  // Tablet: 4 columns
                    md: selectedCard ? 6 : 7,  // Desktop: 6-7 columns
                    lg: selectedCard ? 7 : 9,  // Large: 7-9 columns
                    xl: selectedCard ? 8 : 10, // XL: 8-10 columns
                };
            }
            return 1;
        };

        const columns = calculateGridColumns();
        const gap = cardSpacing * 8; // Convert spacing multiplier to pixels

        return {
            columns,
            cardWidth: baseCardWidth,
            gap,
            minCardWidth: viewMode === 'compact' ? '100px' : '120px',
        };
    }, [preferences, selectedCard]);

    // Memoize filtered navigation to avoid recalculation on every render
    const roleFilteredNavigation = useMemo(
        () => filterNavigationByRole(navigationConfig, roleName),
        [roleName]
    );

    // Apply search filter on top of role filter, keeping pinned cards visible
    const filteredNavigation = useMemo(() => {
        const pinnedIds = preferences.pinnedCards || [];
        const searchFiltered = filterNavigationBySearch(roleFilteredNavigation, debouncedSearchQuery);

        // If there's a search query, include pinned cards even if they don't match
        if (debouncedSearchQuery && pinnedIds.length > 0) {
            const pinnedCards = roleFilteredNavigation.filter(item =>
                pinnedIds.includes(item.id) && !searchFiltered.find(filtered => filtered.id === item.id)
            );
            return [...pinnedCards, ...searchFiltered];
        }

        return searchFiltered;
    }, [roleFilteredNavigation, debouncedSearchQuery, preferences.pinnedCards]);

    // Get favorited items
    const favoritedItems = useMemo(() => {
        const favoriteIds = preferences.favorites || [];
        return roleFilteredNavigation.filter(item => favoriteIds.includes(item.id));
    }, [roleFilteredNavigation, preferences.favorites]);

    // Check token expiration
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
        }
    }, [isAuthenticated, token, logout]);

    // Load user preferences on mount
    useEffect(() => {
        console.log('User preferences loaded:', preferences);
    }, [preferences]);

    // Debounce search query updates (300ms delay)
    useEffect(() => {
        const debouncedUpdate = debounce((query) => {
            setDebouncedSearchQuery(query);
        }, 300);

        debouncedUpdate(searchQuery);

        // Cleanup function to clear timeout on unmount
        return () => {
            // The debounce function handles its own cleanup
        };
    }, [searchQuery]);

    // Inject global styles for animations
    useEffect(() => {
        const styleId = 'theme-global-styles';
        const existingStyle = document.getElementById(styleId);

        if (!existingStyle) {
            const styleElement = document.createElement('style');
            styleElement.id = styleId;
            styleElement.textContent = globalStyles;
            document.head.appendChild(styleElement);
        }

        return () => {
            const styleElement = document.getElementById(styleId);
            styleElement?.remove();
        };
    }, []);

    // Apply theme background to body
    useEffect(() => {
        const applyBodyStyles = () => {
            if (themeStyles.container.background) {
                document.body.style.background = themeStyles.container.background;
            }
            if (themeStyles.container.backgroundSize) {
                document.body.style.backgroundSize = themeStyles.container.backgroundSize;
            }
            if (themeStyles.container.animation) {
                document.body.style.animation = themeStyles.container.animation;
            }
            document.body.style.minHeight = '100vh';
            // Make body overflow conditional based on viewport width
            document.body.style.overflow = window.innerWidth < 768 ? 'auto' : 'hidden';
            document.body.style.transition = 'all 0.3s ease';
        };

        applyBodyStyles();

        // Handle window resize to update overflow dynamically
        const handleResize = () => {
            document.body.style.overflow = window.innerWidth < 768 ? 'auto' : 'hidden';
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.body.style.background = '';
            document.body.style.minHeight = '';
            document.body.style.backgroundSize = '';
            document.body.style.animation = '';
            document.body.style.overflow = '';
        };
    }, [themeStyles]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Detect if user is on Mac
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const modKey = isMac ? event.metaKey : event.ctrlKey;

            // Ctrl+K (Cmd+K) - Focus search
            if (modKey && event.key === 'k') {
                event.preventDefault();
                searchInputRef.current?.focus();
            }

            // Ctrl+, (Cmd+,) - Toggle settings panel
            if (modKey && event.key === ',') {
                event.preventDefault();
                setSettingsPanelOpen(prev => !prev);
            }

            // Escape - Close panels
            if (event.key === 'Escape') {
                if (settingsPanelOpen) {
                    setSettingsPanelOpen(false);
                } else if (searchQuery) {
                    setSearchQuery('');
                    setDebouncedSearchQuery('');
                    searchInputRef.current?.blur();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [settingsPanelOpen, searchQuery]);

    // Handle card click - memoized to prevent unnecessary re-renders
    const handleCardClick = useCallback((item) => {
        const hasSubItems = item.subItems?.length > 0;

        if (hasSubItems) {
            setSelectedCard(prev => prev?.id === item.id ? null : item);
        } else if (item.route) {
            history.push(item.route);
        }
    }, [history]);

    // Handle sub-item click - memoized
    const handleSubItemClick = useCallback((subItem) => {
        if (subItem.route) {
            history.push(subItem.route);
        }
    }, [history]);

    // Handle view mode change
    const handleViewModeChange = useCallback((newMode) => {
        updatePreferences({ viewMode: newMode });
    }, [updatePreferences]);

    // Handle settings panel open/close
    const handleSettingsPanelOpen = useCallback(() => {
        setSettingsPanelOpen(true);
    }, []);

    const handleSettingsPanelClose = useCallback(() => {
        setSettingsPanelOpen(false);
    }, []);

    // Handle search query change
    const handleSearchChange = useCallback((query) => {
        setSearchQuery(query);
    }, []);

    // Handle clear search
    const handleClearSearch = useCallback(() => {
        setSearchQuery('');
        setDebouncedSearchQuery('');
    }, []);

    // Handle favorite toggle
    const handleToggleFavorite = useCallback((itemId) => {
        const currentFavorites = preferences.favorites || [];
        const isFavorite = currentFavorites.includes(itemId);

        const newFavorites = isFavorite
            ? currentFavorites.filter(id => id !== itemId)
            : [...currentFavorites, itemId];

        updatePreferences({ favorites: newFavorites });
    }, [preferences.favorites, updatePreferences]);

    // Handle pin toggle
    const handleTogglePin = useCallback((itemId) => {
        const currentPinned = preferences.pinnedCards || [];
        const isPinned = currentPinned.includes(itemId);

        const newPinned = isPinned
            ? currentPinned.filter(id => id !== itemId)
            : [...currentPinned, itemId];

        updatePreferences({ pinnedCards: newPinned });
    }, [preferences.pinnedCards, updatePreferences]);

    return (
        <>
            <Container
                maxWidth="xl"
                sx={{
                    py: 1,
                    height: { xs: 'auto', md: '100vh' },
                    overflow: { xs: 'auto', md: 'hidden' },
                    transition: 'all 0.3s ease',
                }}
            >
                {/* Page Title and View Mode Selector */}
                <Box sx={{ mb: { xs: 1.5, sm: 1 } }}>
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <Typography
                            variant="h6"
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                fontFamily: 'monospace',
                                // Responsive font sizes
                                fontSize: { xs: '18px', sm: '20px', md: '22px' },
                                mb: 0.5,
                                transition: 'all 0.3s ease',
                                ...themeStyles.pageTitle,
                            }}
                        >
                            Welcome {username}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                // Responsive font sizes
                                fontSize: { xs: '12px', sm: '13px' },
                                transition: 'all 0.3s ease',
                                ...themeStyles.pageDescription,
                            }}
                        >
                            Select a section below to get started
                        </Typography>
                    </Box>
                </Box>

                {/* Main Layout: Cards on Left, Sub-items on Right */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, sm: 2, md: 2 },
                    px: { xs: 1, sm: 2, md: 3 },
                    fontFamily: 'monospace',
                }}>
                    {/* Left Side: Navigation Cards */}
                    <Box sx={{
                        flex: { xs: '1 1 100%', md: selectedCard ? '0 0 70%' : '1 1 100%' },
                        transition: 'all 0.3s ease'
                    }}>
                        {/* Favorites Section - Only show when not searching */}
                        {!debouncedSearchQuery && favoritedItems.length > 0 && (
                            <FavoritesSection
                                favorites={favoritedItems}
                                onCardClick={handleCardClick}
                                onToggleFavorite={handleToggleFavorite}
                                onTogglePin={handleTogglePin}
                                viewMode={preferences.viewMode}
                                cardSize={preferences.cardSize}
                                cardSpacing={preferences.cardSpacing}
                                themeStyles={themeStyles}
                                selectedCardId={selectedCard?.id}
                                pinnedCards={preferences.pinnedCards || []}
                                searchQuery={debouncedSearchQuery}
                                animationsEnabled={preferences.animationsEnabled}
                            />
                        )}

                        {filteredNavigation.length === 0 && debouncedSearchQuery ? (
                            <EmptySearchState
                                searchQuery={debouncedSearchQuery}
                                onClearSearch={handleClearSearch}
                                themeStyles={themeStyles}
                            />
                        ) : (
                            <Box
                                sx={{
                                    display: preferences.viewMode === 'list' ? 'flex' : 'grid',
                                    flexDirection: preferences.viewMode === 'list' ? 'column' : undefined,
                                    // Responsive grid columns using breakpoints
                                    gridTemplateColumns: preferences.viewMode !== 'list'
                                        ? {
                                            xs: `repeat(${gridLayout.columns.xs}, 1fr)`,
                                            sm: `repeat(${gridLayout.columns.sm}, 1fr)`,
                                            md: `repeat(${gridLayout.columns.md}, 1fr)`,
                                            lg: `repeat(${gridLayout.columns.lg}, 1fr)`,
                                            xl: `repeat(${gridLayout.columns.xl}, 1fr)`,
                                        }
                                        : undefined,
                                    // Responsive gap
                                    gap: {
                                        xs: `${Math.max(gridLayout.gap * 0.5, 8)}px`,  // Smaller gap on mobile
                                        sm: `${Math.max(gridLayout.gap * 0.75, 12)}px`, // Medium gap on tablet
                                        md: `${gridLayout.gap}px`,  // Full gap on desktop
                                    },
                                    transition: preferences.animationsEnabled ? 'all 0.3s ease' : 'none',
                                    ...(preferences.animationsEnabled && debouncedSearchQuery && {
                                        '@keyframes fadeInGrid': {
                                            from: { opacity: 0 },
                                            to: { opacity: 1 },
                                        },
                                        animation: 'fadeInGrid 0.4s ease',
                                    }),
                                }}
                            >
                                {filteredNavigation.map((item, index) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            minWidth: gridLayout.minCardWidth,
                                            width: preferences.viewMode === 'list' ? '100%' : 'auto',
                                            transition: preferences.animationsEnabled ? 'all 0.3s ease' : 'none',
                                            ...(preferences.animationsEnabled && debouncedSearchQuery && {
                                                '@keyframes staggerFadeIn': {
                                                    from: {
                                                        opacity: 0,
                                                        transform: 'translateY(10px)',
                                                    },
                                                    to: {
                                                        opacity: 1,
                                                        transform: 'translateY(0)',
                                                    },
                                                },
                                                animation: `staggerFadeIn 0.3s ease ${index * 0.03}s both`,
                                            }),
                                        }}
                                    >
                                        <NavigationCard
                                            icon={item.icon}
                                            title={item.title}
                                            description={item.description}
                                            route={item.route}
                                            subItems={item.subItems}
                                            color={item.color}
                                            onClick={() => handleCardClick(item)}
                                            isSelected={selectedCard?.id === item.id}
                                            themeStyles={themeStyles}
                                            viewMode={preferences.viewMode}
                                            cardSize={preferences.cardSize}
                                            searchQuery={debouncedSearchQuery}
                                            isFavorite={(preferences.favorites || []).includes(item.id)}
                                            isPinned={(preferences.pinnedCards || []).includes(item.id)}
                                            onToggleFavorite={() => handleToggleFavorite(item.id)}
                                            onTogglePin={() => handleTogglePin(item.id)}
                                            animationsEnabled={preferences.animationsEnabled}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Right Side: Sub-items Panel */}
                    {selectedCard && (
                        <Box
                            sx={{
                                // Mobile: Full width, Desktop: 30%
                                flex: { xs: '1 1 100%', md: '30%' },
                                maxHeight: { xs: '400px', md: 'none' },
                                overflowY: { xs: 'auto', md: 'visible' },
                                animation: 'zoomIn 0.4s ease-out',
                                '@keyframes zoomIn': {
                                    from: {
                                        opacity: 0,
                                        transform: 'scale(0.9)',
                                    },
                                    to: {
                                        opacity: 1,
                                        transform: 'scale(1)',
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    // Mobile: Not sticky, Desktop: Sticky
                                    position: { xs: 'relative', md: 'sticky' },
                                    top: { xs: 0, md: 80 },
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    ...themeStyles.card,
                                    borderLeft: `3px solid ${selectedCard.color}`,
                                }}
                            >
                                {/* Panel Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            bgcolor: `${selectedCard.color}20`,
                                            color: selectedCard.color,
                                        }}
                                    >
                                        {React.cloneElement(selectedCard.icon, { sx: { fontSize: 18 } })}
                                    </Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '15px',
                                            ...themeStyles.cardTitle,
                                        }}
                                    >
                                        {selectedCard.title}
                                    </Typography>
                                </Box>

                                {/* Sub-items List */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {selectedCard.subItems.map((subItem, index) => (
                                        <Box
                                            key={subItem.id || index}
                                            onClick={() => handleSubItemClick(subItem)}
                                            sx={{
                                                py: 1,
                                                px: 1.5,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                borderLeft: `2px solid ${subItem.color || selectedCard.color}`,
                                                ...themeStyles.card,
                                                '&:hover': {
                                                    transform: 'translateX(6px)',
                                                    borderLeftWidth: '3px',
                                                    ...themeStyles.card?.['&:hover'],
                                                },
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: '13px',
                                                    ...themeStyles.cardTitle,
                                                }}
                                            >
                                                {subItem.title}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>

            </Container>

            {/* Theme Switcher */}
            <ThemeSwitcher />

            {/* Settings Panel */}
            <SettingsPanel
                open={settingsPanelOpen}
                onClose={handleSettingsPanelClose}
                themeStyles={themeStyles}
                preferences={preferences}
                updatePreferences={updatePreferences}
                resetPreferences={resetPreferences}
            />

            <div style={{
                position: 'fixed',
                bottom: '12px',
                right: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.3rem 0.65rem',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '14px',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
                fontFamily: 'serif',
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '0.7rem',
                backdropFilter: 'blur(10px)'
            }}>
                <CopyrightIcon />
                <b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </>
    );
};

export default Home;